import { useState, useRef } from 'react'
import { Form as FinalForm, FormRenderProps } from 'react-final-form'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import MyTextfield from '../../../control/textfield/textfield'
import useTranslate from '../../../i18n/useTranslate'
import usePreferenceChangePassword, {
    FormProps,
    ChangePasswordProps,
} from '../../../services/um/usePreferenceChangePassword'
import useAuthenStore from '../../../stores/authen/authenStore'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import Dialog, { DialogType } from '../../../control/dialog/dialog'

type FormComponentProps = {
    onRefFormProps?: (func: FormRenderProps) => void
    onRefCloseDialog?: () => void
}
const Form = (props: FormComponentProps) => {
    const { onRefFormProps, onRefCloseDialog } = props
    const t = useTranslate()
    const authenStore = useAuthenStore()
    const preferenceChangePasswordHook = usePreferenceChangePassword()

    const [configDialog, setConfigDialog] =
        useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogTwoRef = useRef<Record<string, Function>>()

    const onRefDialogTwo = (func: Record<string, Function>) => {
        dialogTwoRef.current = func
    }

    const [displayPW, setDisplayPW] = useState({
        password: false,
        confirmPassword: false,
    })

    const validateForm = (values: FormProps) => {
        const errors: any = {}
        if (!values.password) {
            errors.password = t.get('LABEL.REQUIRE_FIELD')
        }
        if (!values.newPassword) {
            errors.newPassword = t.get('LABEL.REQUIRE_FIELD')
        }
        if (values.newPassword !== values.confirmNewPassword) {
            errors.confirmNewPassword = t.get('MESSAGE.M019')
        }
        return errors
    }

    const logout = () => {
        localStorage.clear()
        setTimeout(() => {
            window.location.replace(process.env.PUBLIC_URL)
        }, 1000)
    }

    const handleSubmitForm = async (value: FormProps) => {
        const param: ChangePasswordProps = {
            userId: authenStore.profile.userId ?? 0,
            password: value.password,
            newPassword: value.newPassword,
        }
        const result = await preferenceChangePasswordHook.putPreferencesChangePassword(param)
        if (result.message === 'success') {
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'alert-message',
                onAfterClose: () => {
                    onRefCloseDialog?.()
                    logout()
                },
            })
            dialogTwoRef.current?.openDialog()
        } else {
            setConfigDialog({
                title: t.get('ALERT.TITLE.ERROR'),
                content: t.get('ALERT.CONTENT.SAVE_NOT_SUCCESS'),
                type: 'alert-message',
                onAfterClose: onRefCloseDialog,
            })
            dialogTwoRef.current?.openDialog()
        }
    }

    const handleClickDisplayPassword = () => {
        setDisplayPW((prev) => ({ ...prev, password: !prev.password }))
    }

    const handleClickDisplayConfirmPassword = () => {
        setDisplayPW((prev) => ({ ...prev, confirmPassword: !prev.confirmPassword }))
    }

    return (
        <FinalForm onSubmit={handleSubmitForm} validate={validateForm}>
            {(props: FormRenderProps) => {
                if (onRefFormProps) onRefFormProps(props)
                return (
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <MyTextfield
                                name="password"
                                maxLength={500}
                                type={displayPW.password ? 'text' : 'password'}
                                materialInputProps={{
                                    disabled: false,
                                    label: `${t.get('UM.PROFILE.PASSWORD')}`,
                                    placeholder: `${t.get('UM.PROFILE.PASSWORD')}`,
                                    size: 'small',
                                }}
                                icon={
                                    <IconButton size="small" onClick={handleClickDisplayPassword}>
                                        {displayPW.password ? (
                                            <Visibility style={{ fontSize: '1.2rem' }} />
                                        ) : (
                                            <VisibilityOff style={{ fontSize: '1.2rem' }} />
                                        )}
                                    </IconButton>
                                }
                                handleValueChange={(val: string) => {
                                    if (val) {
                                        const txtLogin = val.replace(/[ก-๙]|\s/g, '')
                                        props.form.change('password', txtLogin)
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <MyTextfield
                                name="newPassword"
                                maxLength={500}
                                type={displayPW.confirmPassword ? 'text' : 'password'}
                                materialInputProps={{
                                    disabled: false,
                                    label: `${t.get('UM.PROFILE.NEW_PASSWORD')}`,
                                    placeholder: `${t.get('UM.PROFILE.NEW_PASSWORD')}`,
                                    size: 'small',
                                }}
                                icon={
                                    <IconButton size="small" onClick={handleClickDisplayConfirmPassword}>
                                        {displayPW.confirmPassword ? (
                                            <Visibility style={{ fontSize: '1.2rem' }} />
                                        ) : (
                                            <VisibilityOff style={{ fontSize: '1.2rem' }} />
                                        )}
                                    </IconButton>
                                }
                                handleValueChange={(val: string) => {
                                    if (val) {
                                        const txtLogin = val.replace(/[ก-๙]|\s/g, '')
                                        props.form.change('newPassword', txtLogin)
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <MyTextfield
                                name="confirmNewPassword"
                                maxLength={500}
                                type={displayPW.confirmPassword ? 'text' : 'password'}
                                materialInputProps={{
                                    disabled: false,
                                    label: `${t.get('UM.PROFILE.CONFIRM_NEW_PASSWORD')}`,
                                    placeholder: `${t.get('UM.PROFILE.CONFIRM_NEW_PASSWORD')}`,
                                    size: 'small',
                                }}
                                icon={
                                    <IconButton size="small" onClick={handleClickDisplayConfirmPassword}>
                                        {displayPW.confirmPassword ? (
                                            <Visibility style={{ fontSize: '1.2rem' }} />
                                        ) : (
                                            <VisibilityOff style={{ fontSize: '1.2rem' }} />
                                        )}
                                    </IconButton>
                                }
                                handleValueChange={(val: string) => {
                                    if (val) {
                                        const txtLogin = val.replace(/[ก-๙]|\s/g, '')
                                        props.form.change('confirmNewPassword', txtLogin)
                                    }
                                }}
                            />
                        </Grid>
                        <Dialog onRef={onRefDialogTwo} {...configDialog} />
                    </Grid>
                )
            }}
        </FinalForm>
    )
}

export default Form
